import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  PhoneAuthProvider,
  multiFactor,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  getMultiFactorResolver,
  getRedirectResult,
  onIdTokenChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  verifyPasswordResetCode,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type MultiFactorError,
  type MultiFactorResolver,
  // The narrowed hint. `MultiFactorInfo` is the base and carries no
  // `phoneNumber`; the cast is guarded by the `factorId` check above it, which
  // is the only thing that distinguishes the two at runtime.
  type PhoneMultiFactorInfo,
  type MultiFactorInfo,
  type MultiFactorSession,
  type User,
  type UserCredential,
} from 'firebase/auth'
import { KNOWN_CODES, REQUIRES_RECENT_LOGIN, authErrorFor } from './errors'
import {
  AuthFailure,
  MfaRequiredError,
  type AuthPort,
  type AuthUser,
  type SocialSignIn,
  type MfaChallenge,
  type MfaFactor,
  type EnrolledFactor,
  type SignInProvider,
  type TotpEnrolment,
  type PhoneEnrolment,
} from './port'
import { __setSink, addIdentitySink } from '../platform/analytics/analytics'
import { scrubUrl } from '../platform/analytics/sentryScrub'

/**
 * The four public values from the Firebase console's web app.
 *
 * A parameter rather than a read of `src/config/env.ts`, so this module has no
 * opinion about where they come from and the composition root in `main.tsx`
 * stays the only place `env.firebase` is touched. It also keeps this file
 * importable in a test without the environment existing.
 */
export type FirebaseConfig = {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
  /**
   * C4, optional: `getAnalytics` reads it from the app's options, and without it
   * the SDK falls back to a dynamic-config fetch. Absent means analytics is off.
   */
  measurementId?: string
}

/**
 * Is this Firebase telling us something, or is it an ordinary throw?
 *
 * Duck-typed rather than `instanceof FirebaseError`, deliberately. Two copies
 * of `@firebase/util` in a tree — a transitive dependency pinning a different
 * minor, which is ordinary — give two distinct `FirebaseError` classes, and
 * `instanceof` against the wrong one is false for an error that carries a
 * perfectly good code. The failure mode of that mistake is the exact thing this
 * module exists to prevent: an unmapped error reaching a form.
 *
 * `auth/` is checked as well as the type, because `code` is a common field name
 * — a fetch `AbortError` has one — and `authErrorFor` would turn anything else
 * into an unclassified failure with a misleading `code` in the log.
 */
function firebaseCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null
  const code: unknown = (error as { code?: unknown }).code
  return typeof code === 'string' && code.startsWith('auth/') ? code : null
}

/**
 * The one translation. Every `catch` in this file routes through it.
 *
 * `authErrorFor` refuses to guess a field for a code it does not know, so an
 * unrecognised code and a `TypeError` out of fetch both land on `form` with the
 * unclassified message rather than on an input the user has not got wrong.
 */
function asAuthFailure(error: unknown): AuthFailure {
  const code = firebaseCode(error)
  // `unexpected` is not a Firebase code and is not in KNOWN_CODES, which is the
  // point: it produces the unclassified message and a log line that says the
  // throw had no code at all.
  const detail = authErrorFor(code ?? 'unexpected')
  /**
   * The one place an unclassified failure is still legible.
   *
   * `authErrorFor` keeps the code on `detail.code` and every screen renders only
   * `detail.message` — so until this line, the single fact that identifies an
   * unmapped failure was captured and then thrown away. Diagnosing
   * `auth/totp-challenge-timeout` took a Network-tab excavation and a grep
   * through the SDK bundle, and the message the user saw was identical to the
   * one a dead socket produces.
   *
   * `warn` rather than `error`: the failure is already reported to the user on
   * the surface that caused it, and this is the developer's copy. It fires in
   * production too, deliberately — an unmapped code is most interesting exactly
   * where we cannot reproduce it, and it carries no PII: Firebase's `code` and
   * its own English sentence, never a credential or an address.
   */
  if (!(detail.code in KNOWN_CODES)) {
    console.warn(
      `[auth] unmapped failure — the user saw the unclassified message.\n` +
        `  code: ${detail.code}\n` +
        `  firebase: ${error instanceof Error ? error.message : String(error)}\n` +
        `  Map it in src/auth/errors.ts KNOWN_CODES if a user can act on it.`,
    )
  }
  return new AuthFailure(detail)
}

/** Runs an SDK call and lets nothing but the app's own errors out of it. */
async function mapped<T>(run: () => Promise<T>, auth: Auth): Promise<T> {
  try {
    return await run()
  } catch (error) {
    if (firebaseCode(error) === 'auth/multi-factor-auth-required') {
      // The resolver holds the half-finished sign-in. It is the ONLY way to
      // complete it — `openapi.json` has no `GET /mfa/challenges/{token}`, and
      // the intake decision was Firebase-sourced factors only.
      throw new MfaRequiredError(
        challengeFrom(auth, getMultiFactorResolver(auth, error as MultiFactorError)),
      )
    }
    throw asAuthFailure(error)
  }
}

/**
 * A Firebase user, reduced to what the app is allowed to know.
 *
 * Three fields, and the reduction is the port's whole point: a `User` also
 * carries `refreshToken`, `providerData` and a dozen methods, and a screen that
 * received one would start depending on the SDK through the interface that
 * exists to stop exactly that.
 */
/**
 * Did this operation CREATE the account?
 *
 * The one thing about a social sign-in that nothing above the port can work
 * out: Google sign-up and Google sign-in are a single Firebase operation, so a
 * screen sees the same resolved promise either way. `getAdditionalUserInfo`
 * reads it off the credential, and only this module ever holds one.
 *
 * `?? false` rather than optional: an absent `additionalUserInfo` means
 * Firebase did not say, and "we do not know" has to resolve to "not a signup"
 * — reporting a signup we cannot evidence is the defect this exists to fix,
 * in the direction that inflates the number.
 */
function outcomeOf(credential: UserCredential | null): SocialSignIn | null {
  if (credential === null) return null
  return { isNewUser: getAdditionalUserInfo(credential)?.isNewUser ?? false }
}

function toAuthUser(user: User): AuthUser {
  return { uid: user.uid, email: user.email, emailVerified: user.emailVerified }
}

/**
 * Which enrolled factors this client can actually clear.
 *
 * **Both Firebase kinds, since #171.** Phone used to be filtered out here,
 * because clearing an SMS factor needs `PhoneAuthProvider.verifyPhoneNumber`
 * with a `RecaptchaVerifier` to run *before* the user has a code to type, and
 * `submit(factorId, code)` had no moment in which to do that. The port now has
 * one — `prepare` — so the factor is offered rather than refused.
 *
 * An unrecognised `factorId` from a future SDK is still dropped, and still
 * deliberately NOT mapped to a kind we would be guessing at. That is the case
 * `MfaChallenge`'s empty-list copy covers without naming a cause.
 *
 * Backend-sourced factors — biometric, push — never reach here at all: they are
 * enrolled with our own API, not with Firebase, so the resolver has never heard
 * of them and Firebase raises no challenge for them.
 */
function factorsFrom(resolver: MultiFactorResolver): MfaFactor[] {
  // The annotation is load-bearing: without it `flatMap` infers a union of two
  // array types rather than one array of the union, and the phone arm is
  // rejected against the totp arm's literal `kind`.
  return resolver.hints.flatMap((hint): MfaFactor[] => {
    if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
      return [
        {
          id: hint.uid,
          kind: 'totp' as const,
          // What the user called it when enrolling. Never a phone number.
          hint: hint.displayName ?? null,
          // The authenticator is already showing a code.
          needsDelivery: false,
        },
      ]
    }
    if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
      return [
        {
          id: hint.uid,
          kind: 'phone' as const,
          // Firebase masks this itself — `+*******4417`, never the full number
          // — and it is the only thing that tells two phone factors apart. It
          // reaches the label and stops there: `factor_type` carries the KIND
          // to analytics, and vocabulary.ts admits no key that could carry a
          // hint. `displayName` is the fallback for a factor enrolled without
          // one being returned.
          hint: (hint as PhoneMultiFactorInfo).phoneNumber ?? hint.displayName ?? null,
          // Nothing has been sent. `prepare` is what sends it.
          needsDelivery: true,
        },
      ]
    }
    return []
  })
}

function challengeFrom(auth: Auth, resolver: MultiFactorResolver): MfaChallenge {
  /**
   * The id Firebase gives back when the SMS goes out, per factor.
   *
   * Keyed by factor rather than held as one value: an account with two phone
   * factors would otherwise have a second "send code" overwrite the first, and
   * the code from the first message would then be checked against the second
   * number. Replacing the entry for the SAME factor is correct and is how
   * "send me a new code" works.
   */
  const verifications = new Map<string, string>()

  return {
    factors: factorsFrom(resolver),
    /**
     * Empty, and no longer computed.
     *
     * Both kinds Firebase can raise are presentable now, so there is nothing to
     * report. The FIELD stays because the contract does: an adapter that
     * filters a factor out must say what it filtered, which is what stopped
     * this screen telling SMS users their account used Face ID. A future
     * adapter that starts filtering again has somewhere to say so.
     */
    unpresentable: [],
    async prepare(factorId, container) {
      const hint = resolver.hints.find((h) => h.uid === factorId)
      if (hint === undefined) {
        throw new AuthFailure(authErrorFor('auth/multi-factor-info-not-found'))
      }
      // TOTP needs nothing brought into existence. Returning quietly rather
      // than throwing: the screen is allowed to call this for any factor, and
      // `needsDelivery` is the field that says whether it should bother.
      if (hint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID) return

      // Declared out here and CONSTRUCTED inside the try. The constructor
      // validates its container and throws `auth/argument-error` for a detached
      // node or one that already holds a widget — and outside the try that
      // FirebaseError crossed the port raw, which the header of this module
      // says cannot happen. It degraded quietly rather than crashing: the
      // screen mapped it to the unclassified message, where a mapped one exists.
      let verifier: RecaptchaVerifier | undefined
      try {
        verifier = new RecaptchaVerifier(auth, container, { size: 'invisible' })
        const id = await new PhoneAuthProvider(auth).verifyPhoneNumber(
          { multiFactorHint: hint, session: resolver.session },
          verifier,
        )
        verifications.set(factorId, id)
      } catch (error) {
        throw asAuthFailure(error)
      } finally {
        // Always, including on the failure path. The widget holds a live
        // reCAPTCHA client keyed to a container this screen may re-render or
        // unmount; leaving it attached makes the NEXT `prepare` on the same
        // container fail with an SDK error about an already-rendered widget,
        // which reads to the user as "resend is broken".
        // `?.` because the constructor itself can now be what threw.
        verifier?.clear()
        /**
         * And then empty the container, because `clear()` does not.
         *
         * `RecaptchaVerifier.clear()` tears down the grecaptcha client but
         * leaves the DOM it injected — the widget `<div>` and its challenge
         * `<iframe>` — parented to the container we handed it. The NEXT
         * `new RecaptchaVerifier` on a non-empty container throws
         * `auth/argument-error`, which is unmapped and therefore reaches the
         * user as the unclassified message. The shape of the bug is that the
         * FIRST send works and every later one on the same mounted screen does
         * not, so it reads as "resend is broken" — the exact failure the
         * `clear()` above was added to prevent, and only half prevented.
         *
         * Safe unconditionally: the container is a div this app renders empty
         * and nothing else writes to, so there is never anything here worth
         * keeping. When `clear()` did remove the nodes this is a no-op.
         *
         * It also restores `.mfa-recaptcha:empty { display: none }` — with
         * orphaned nodes the selector stops matching and the card carries 16px
         * of dead gap for the rest of its life.
         */
        container.replaceChildren()
      }
    },
    prepared(factorId) {
      return verifications.has(factorId)
    },
    async submit(factorId, code) {
      const hint = resolver.hints.find((h) => h.uid === factorId)
      // Refused rather than falling back to `hints[0]`: submitting one factor's
      // assertion for another is a failure the user cannot diagnose, and a
      // caller passing an id we did not offer is a bug rather than a typo.
      if (hint === undefined) {
        throw new AuthFailure(authErrorFor('auth/multi-factor-info-not-found'))
      }
      try {
        if (hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
          await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code))
          return
        }
        if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
          const verificationId = verifications.get(factorId)
          // No SMS was sent, so there is nothing this code could be. Its own
          // code rather than the unclassified message, because the recovery is
          // specific and the user can act on it: ask for a code.
          if (verificationId === undefined) {
            throw new AuthFailure(authErrorFor('auth/missing-verification-id'))
          }
          await resolver.resolveSignIn(
            PhoneMultiFactorGenerator.assertion(
              PhoneAuthProvider.credential(verificationId, code),
            ),
          )
          return
        }
        throw new AuthFailure(authErrorFor('auth/multi-factor-info-not-found'))
      } catch (error) {
        // Already ours — mapping it again would replace a specific message with
        // the unclassified one, since an `AuthFailure` carries no `auth/` code.
        if (error instanceof AuthFailure) throw error
        // Not `mapped`: a second `multi-factor-auth-required` cannot arrive
        // here, and treating one as a fresh challenge would loop.
        throw asAuthFailure(error)
      }
    },
  }
}

/**
 * The only module in this repo that imports `firebase/*`. A test asserts it.
 *
 * Everything above the port speaks the app's vocabulary: `AuthUser`,
 * `AuthFailure` with the field that caused it, `MfaRequiredError`. If a
 * `FirebaseError` ever escapes this file, M1-R2's mapping has to be repeated at
 * every form — and the fake the whole suite runs against stops being a faithful
 * double of the real thing.
 */
/** How long a completed re-authentication licenses a last-factor removal by the same user, in this tab. */
const REAUTH_FRESH_MS = 5 * 60_000

/** Which way THIS account can prove itself again. A plain function, not a method. */
function reauthMethodOf(auth: Auth): 'password' | 'google' | null {
  const providers = auth.currentUser?.providerData.map((p) => p.providerId) ?? []
  // Password first: an account with both can use either, and the password is
  // the one that needs no popup (which a blocker can eat).
  if (providers.includes(EmailAuthProvider.PROVIDER_ID)) return 'password'
  if (providers.includes(GoogleAuthProvider.PROVIDER_ID)) return 'google'
  return null
}

/**
 * Enrolment needs a signed-in user, and every entry point says so the same way.
 *
 * `auth/no-current-user` rather than a bare throw: the session can end between
 * rendering Settings and pressing a button on it, and that is a sentence the
 * user can act on rather than an unclassified failure.
 */
function currentUser(auth: Auth) {
  const user = auth.currentUser
  if (user === null) throw new AuthFailure(authErrorFor('auth/no-current-user'))
  return user
}

/** Firebase's enrolled-factor shape, in the app's vocabulary. Unknown kinds drop. */
function enrolledFrom(info: MultiFactorInfo): EnrolledFactor[] {
  const common = {
    id: info.uid,
    // Firebase reports this as a UTC string; `null` where it reported nothing
    // rather than inventing "just now".
    enrolledAt: info.enrollmentTime ?? null,
  }
  if (info.factorId === TotpMultiFactorGenerator.FACTOR_ID) {
    return [{ ...common, kind: 'totp' as const, label: info.displayName ?? null }]
  }
  if (info.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
    return [
      {
        ...common,
        kind: 'phone' as const,
        // The number beats a display name here: it is what tells two phones
        // apart. **It is NOT masked**, which an earlier version of this comment
        // claimed — measured against real Firebase, `enrolledFactors` returns
        // the full E.164 number. Only the CHALLENGE hints are masked
        // (`+*******3434`), because those are shown before the user has proved
        // who they are. Here they already have, and it is their own number on
        // their own settings page — which is also the only way to tell which of
        // two phones a row refers to. It reaches the label and stops: analytics
        // carries `factor_type` and nothing else.
        label: (info as PhoneMultiFactorInfo).phoneNumber ?? info.displayName ?? null,
      },
    ]
  }
  return []
}

export function createFirebaseAuthPort(config: FirebaseConfig): AuthPort {
  // `initializeApp` is not idempotent across distinct config objects, and React
  // Fast Refresh re-evaluates this module in development. Reusing the default
  // app is what keeps a second port from tearing down the first one's listeners.
  //
  // But reuse must not become "silently serve whatever project happens to be
  // initialised". A mismatch here would authenticate every user against the
  // wrong Firebase project while looking entirely healthy — the failure mode is
  // a user base split in two, discovered later and not fixable afterwards. It
  // cannot happen while this is the only importer of `firebase/*`, which is the
  // point: the guard is what makes that a checked property rather than a
  // standing assumption.
  const existing = getApps().length > 0 ? getApp() : null
  if (existing !== null && existing.options.projectId !== config.projectId) {
    throw new Error(
      `Firebase is already initialised for project "${String(existing.options.projectId)}", ` +
        `and this port was built for "${config.projectId}". Refusing to sign users in ` +
        `against a project this app did not configure.`,
    )
  }
  const app = existing ?? initializeApp(config)
  const auth = getAuth(app)

  /**
   * The licence to remove a last factor: WHO re-authenticated and WHEN, or null.
   *
   * Keyed to the uid, because one port serves the tab for its whole life and a
   * second user signing in must not inherit the first's. Ended by `signOut`,
   * by the token listener seeing another uid, and by a removal that succeeds;
   * kept across one that FAILS, so a retry does not need a second trip through
   * a second factor whose resolver is already used. No `purpose`: a
   * re-authentication proves identity, not intent, and recording one would
   * widen the port for a window the uid and the 5 minutes already bound.
   */
  let licence: { uid: string; at: number } | null = null
  const grant = () => {
    const uid = auth.currentUser?.uid
    licence = uid === undefined ? null : { uid, at: Date.now() }
  }
  const licensed = () =>
    licence !== null &&
    licence.uid === auth.currentUser?.uid &&
    Date.now() - licence.at < REAUTH_FRESH_MS

  return {
    async signIn(email, password) {
      await mapped(() => signInWithEmailAndPassword(auth, email, password), auth)
    },

    async signUp(email, password) {
      await mapped(() => createUserWithEmailAndPassword(auth, email, password), auth)
    },

    async signInWithGoogle(mode) {
      const provider = new GoogleAuthProvider()
      if (mode === 'redirect') {
        // Resolves only because the navigation has not happened yet; the
        // document is replaced moments later, so there is no outcome to
        // report. It arrives via `completeRedirect` on the next document.
        await mapped(() => signInWithRedirect(auth, provider), auth)
        return null
      }
      return outcomeOf(await mapped(() => signInWithPopup(auth, provider), auth))
    },

    async completeRedirect() {
      // NOT memoised, and that is load-bearing. Firebase clears the pending
      // credential on the first call, so a second one legitimately returns
      // null — which is what keeps `<StrictMode>`'s double effect from emitting
      // `signin_completed` twice in development. Caching the `true` would
      // double-count every social sign-in, silently and only there. See the
      // port's own comment.
      return outcomeOf(await mapped(() => getRedirectResult(auth), auth))
    },

    enrolledFactors() {
      const user = auth.currentUser
      // `[]` rather than a throw: Settings can render mid-sign-out, and "no
      // factors" is the truthful answer for nobody.
      if (user === null) return []
      return multiFactor(user).enrolledFactors.flatMap(enrolledFrom)
    },

    identity() {
      const user = auth.currentUser
      if (user === null) return { providers: [], unrecognised: [], phoneNumber: null }
      const providers: SignInProvider[] = []
      const unrecognised: string[] = []
      for (const { providerId } of user.providerData) {
        if (providerId === EmailAuthProvider.PROVIDER_ID) providers.push('password')
        else if (providerId === GoogleAuthProvider.PROVIDER_ID) providers.push('google')
        else unrecognised.push(providerId)
      }
      return { providers, unrecognised, phoneNumber: user.phoneNumber ?? null }
    },

    async beginTotpEnrolment(): Promise<TotpEnrolment> {
      const user = currentUser(auth)
      try {
        const session: MultiFactorSession = await multiFactor(user).getSession()
        const secret = await TotpMultiFactorGenerator.generateSecret(session)
        return {
          secret: secret.secretKey,
          // The account name is what the authenticator app lists, so it has to
          // identify the ACCOUNT; the issuer is what groups TrackE5's entries.
          uri: secret.generateQrCodeUrl(user.email ?? user.uid, 'TrackE5'),
          async complete(code, label) {
            try {
              await multiFactor(user).enroll(
                TotpMultiFactorGenerator.assertionForEnrollment(secret, code),
                label,
              )
            } catch (error) {
              throw asAuthFailure(error)
            }
          },
        }
      } catch (error) {
        if (error instanceof AuthFailure) throw error
        throw asAuthFailure(error)
      }
    },

    async beginPhoneEnrolment(phoneNumber, container): Promise<PhoneEnrolment> {
      const user = currentUser(auth)
      // Declared out here and constructed inside the try, for the reason
      // `MfaChallenge.prepare` gives: the constructor validates its container
      // and its throw would otherwise cross the port unmapped.
      let verifier: RecaptchaVerifier | undefined
      try {
        const session = await multiFactor(user).getSession()
        verifier = new RecaptchaVerifier(auth, container, { size: 'invisible' })
        const verificationId = await new PhoneAuthProvider(auth).verifyPhoneNumber(
          { phoneNumber, session },
          verifier,
        )
        return {
          async complete(code, label) {
            try {
              await multiFactor(user).enroll(
                PhoneMultiFactorGenerator.assertion(
                  PhoneAuthProvider.credential(verificationId, code),
                ),
                label,
              )
            } catch (error) {
              throw asAuthFailure(error)
            }
          },
        }
      } catch (error) {
        if (error instanceof AuthFailure) throw error
        throw asAuthFailure(error)
      } finally {
        verifier?.clear()
      }
    },

    async removeFactor(factorId) {
      const user = currentUser(auth)
      // Read live: the guard below is only as good as the count it reads, and
      // the cached one can be as old as the page.
      try {
        await user.reload()
      } catch (error) {
        throw asAuthFailure(error)
      }
      const info = multiFactor(user).enrolledFactors.find((f) => f.uid === factorId)
      // Refused rather than passed through. `unenroll` accepts a bare id, so a
      // wrong one would reach Firebase and fail with a code the user cannot act
      // on; this names the real situation.
      if (info === undefined) {
        throw new AuthFailure(authErrorFor('auth/multi-factor-info-not-found'))
      }
      // The step-up rule, in the adapter so no screen can skip it: the LAST
      // factor goes only under a live licence (this user, this tab, 5 minutes).
      // Client-side — server enforcement is #189.
      if (multiFactor(user).enrolledFactors.length <= 1 && !licensed()) {
        throw new AuthFailure(authErrorFor(REQUIRES_RECENT_LOGIN))
      }
      try {
        await multiFactor(user).unenroll(info)
        licence = null
      } catch (error) {
        throw asAuthFailure(error)
      }
    },

    reauthMethod: () => reauthMethodOf(auth),

    async reauthenticate(password) {
      const user = currentUser(auth)
      try {
        // `mapped`, so an MFA account's `multi-factor-auth-required` becomes a
        // `MfaRequiredError` whose `submit` resolves THIS re-authentication.
        if (reauthMethodOf(auth) === 'google') {
          await mapped(() => reauthenticateWithPopup(user, new GoogleAuthProvider()), auth)
        } else {
          if (user.email === null || password === undefined) {
            throw new AuthFailure(authErrorFor('auth/missing-password'))
          }
          const email = user.email
          await mapped(
            () =>
              reauthenticateWithCredential(user, EmailAuthProvider.credential(email, password)),
            auth,
          )
        }
        grant()
      } catch (error) {
        if (!(error instanceof MfaRequiredError)) throw error
        const { challenge } = error
        // The re-authentication is complete only when the SECOND factor is
        // accepted, so that is where it becomes fresh.
        throw new MfaRequiredError({
          ...challenge,
          async submit(factorId, code) {
            await challenge.submit(factorId, code)
            grant()
          },
        })
      }
    },

    async sendPasswordReset(email) {
      await mapped(() => sendPasswordResetEmail(auth, email), auth)
    },

    async verifyResetCode(oobCode) {
      return await mapped(() => verifyPasswordResetCode(auth, oobCode), auth)
    },

    async completeReset(oobCode, newPassword) {
      await mapped(() => confirmPasswordReset(auth, oobCode, newPassword), auth)
    },

    async sendVerificationEmail() {
      const user = auth.currentUser
      // Checked here rather than left to the SDK: `sendEmailVerification`
      // requires a user and there is no Firebase code for "you passed nothing".
      // The session has gone in the meantime, and saying so beats the
      // unclassified message.
      if (user === null) throw new AuthFailure(authErrorFor('auth/no-current-user'))
      await mapped(() => sendEmailVerification(user), auth)
    },

    async signOut() {
      licence = null
      await mapped(() => signOut(auth), auth)
    },

    async getIdToken() {
      const user = auth.currentUser
      if (user === null) return null
      try {
        return await user.getIdToken()
      } catch {
        // `client.ts`'s contract: an unauthenticated request is a request
        // WITHOUT the header, not a crash. This provider runs on every request,
        // so a throw here would surface as an unhandled rejection inside the
        // middleware instead of the 401 the app already knows how to handle.
        return null
      }
    },

    onUserChanged(listener) {
      // `onIdTokenChanged`, NOT `onAuthStateChanged`. The token provider is
      // resolved per request and Firebase tokens expire hourly;
      // `onAuthStateChanged` does not fire on a refresh, so the provider would
      // keep handing out a token about to 401 — and `emailVerified` flipping to
      // true, which is how M1-R4's banner clears, arrives on exactly that event.
      return onIdTokenChanged(auth, (user) => {
        if (user === null || user.uid !== licence?.uid) licence = null
        listener(user === null ? null : toAuthUser(user))
      })
    },
  }
}

/**
 * What gtag may know about the page: the URL with ids/tokens masked and the
 * query/fragment dropped (`scrubUrl`), a referrer scrubbed the same way, and a
 * fixed title — `document.title` can name a merchant or an account.
 * Read per call: an SPA's URL moves.
 */
export function neutralPage(): { page_location: string; page_title: string; page_referrer?: string } {
  const page = { page_location: scrubUrl(window.location.href), page_title: 'TrackE5' }
  return document.referrer ? { ...page, page_referrer: scrubUrl(document.referrer) } : page
}

/**
 * Firebase Analytics as the analytics sink (#112). Lazy: `firebase/analytics`
 * is a separate chunk that only loads when a `measurementId` exists.
 *
 * **Never throws and never rejects.** `isSupported()` is false in a browser with
 * no cookies/IndexedDB, and a blocked gtag script surfaces later as a failed
 * request rather than a throw here — either way the page must carry on. Returns
 * whether the sink was installed.
 *
 * `emit()` has already validated every property before the sink sees it, so
 * `logEvent` receives only the guarded copy (rule 7).
 */
export async function initAnalytics(app: FirebaseApp): Promise<boolean> {
  try {
    if (!app.options.measurementId) return false
    const { initializeAnalytics, isSupported, logEvent, setUserId } = await import('firebase/analytics')
    if (!(await isSupported())) return false
    // `getAnalytics` would leave gtag's automatic page_view ON, and Google would
    // receive `page_location`/`page_title` — `/verify/:token`, `/invite/:token`,
    // `?oobCode=&apiKey=` — which `emit`'s property guard never sees because
    // gtag adds them itself. So: no automatic page_view, and the config call
    // (which also decorates first_visit/session_start) carries scrubbed values.
    const analytics = initializeAnalytics(app, { config: { send_page_view: false, ...neutralPage() } })
    __setSink((name, props) => {
      try {
        // Page fields LAST so a property can never override the scrubbed ones.
        logEvent(analytics, name, { ...props, ...neutralPage() })
      } catch {
        // Lost beats crashed.
      }
    })
    addIdentitySink((id) => {
      try {
        setUserId(analytics, id)
      } catch {
        // as above
      }
    })
    return true
  } catch {
    return false
  }
}

/** The app `createFirebaseAuthPort` initialised, for `startTelemetry`. */
export function currentFirebaseApp(): FirebaseApp | null {
  return getApps().length > 0 ? getApp() : null
}
